import axios from 'axios';

/**
 * API helper functions for authentication actions
 */

export interface SendVerificationEmailRequest {
  email: string;
  source?: string;
}

export interface VerifyEmailRequest {
  action_code: string;
  email?: string;
  firebase_uid?: string;
}

export interface VerifyPasswordResetCodeRequest {
  action_code: string;
  email?: string;
}

export interface ResetPasswordRequest {
  action_code: string;
  new_password: string;
  email?: string;
}

export interface RecoverEmailRequest {
  action_code: string;
  email?: string;
  firebase_uid?: string;
}

export interface AuthActionResponse {
  success: boolean;
  message: string;
  email?: string;
}

/**
 * Send a verification email to the user
 */
export async function sendVerificationEmail(
  email: string,
  source?: string
): Promise<AuthActionResponse> {
  try {
    const response = await axios.post<AuthActionResponse>(
      '/auth/send-verification-email',
      { email, source } as SendVerificationEmailRequest
    );
    return response.data;
  } catch (error: any) {
    // Re-throw with more context
    if (error.response) {
      // Server responded with error
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Unknown server error';
      throw new Error(errorMessage);
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Could not reach server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Failed to send verification email');
    }
  }
}

/**
 * Verify an email address using an action code
 * Note: Frontend should verify the code first using applyActionCode(),
 * then call this endpoint with email/uid to track the event.
 */
export async function verifyEmail(
  actionCode: string,
  email?: string,
  firebaseUid?: string
): Promise<AuthActionResponse> {
  const response = await axios.post<AuthActionResponse>(
    '/auth/verify-email',
    {
      action_code: actionCode,
      email,
      firebase_uid: firebaseUid,
    } as VerifyEmailRequest
  );
  return response.data;
}

/**
 * Verify a password reset code and track initiation
 * Note: Frontend should verify the code first using verifyPasswordResetCode(),
 * then call this endpoint with email to track the event.
 */
export async function verifyPasswordResetCode(
  actionCode: string,
  email?: string
): Promise<AuthActionResponse> {
  const response = await axios.post<AuthActionResponse>(
    '/auth/verify-password-reset-code',
    {
      action_code: actionCode,
      email,
    } as VerifyPasswordResetCodeRequest
  );
  return response.data;
}

/**
 * Reset a user's password
 * Note: Frontend should verify the action code first using verifyPasswordResetCode(),
 * then call this endpoint with email and new password.
 */
export async function resetPassword(
  actionCode: string,
  newPassword: string,
  email?: string
): Promise<AuthActionResponse> {
  const response = await axios.post<AuthActionResponse>(
    '/auth/reset-password',
    {
      action_code: actionCode,
      new_password: newPassword,
      email,
    } as ResetPasswordRequest
  );
  return response.data;
}

/**
 * Recover an email address using an action code
 * Note: Frontend should verify the code first using applyActionCode(),
 * then call this endpoint with email/uid to track the event.
 */
export async function recoverEmail(
  actionCode: string,
  email?: string,
  firebaseUid?: string
): Promise<AuthActionResponse> {
  const response = await axios.post<AuthActionResponse>(
    '/auth/recover-email',
    {
      action_code: actionCode,
      email,
      firebase_uid: firebaseUid,
    } as RecoverEmailRequest
  );
  return response.data;
}

